import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { MarketplacePreference, Sport } from '__generated__/globalTypes';
import { Switch } from 'atoms/inputs/Switch';
import { LoadingIndicator } from 'atoms/loader/LoadingIndicator';
import { BodyM } from 'atoms/typography';
import { ConfirmDialog } from 'components/form/ConfirmDialog';
import { useQuery } from 'hooks/graphql/useQuery';
import { useUpdateUserProfile } from 'hooks/useUpdateUserProfile';
import { getFormattedSport } from 'lib/sports';

import { SettingsSection } from '../SettingsSection';
import {
  AcceptCashOffersQuery,
  AcceptCashOffersQueryVariables,
} from './__generated__/index.graphql';

const ACCEPT_CASH_OFFERS_QUERY = gql`
  query AcceptCashOffersQuery {
    currentUser {
      slug
      profile {
        id
        marketplacePreferences(sports: [FOOTBALL, NBA, BASEBALL]) {
          sport
          preferences {
            name
            value
          }
        }
      }
    }
  }
` as TypedDocumentNode<AcceptCashOffersQuery, AcceptCashOffersQueryVariables>;

const messages = defineMessages({
  title: {
    id: 'Settings.AcceptCashOnly.onlyAcceptMoneyOffers',
    defaultMessage: 'Only accept cash offers',
  },
  description: {
    id: 'Settings.AcceptCashOnly.description',
    defaultMessage:
      'Prevent managers to propose cards as part of a trade to you, only money.',
  },
});

const Option = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const AcceptCashOnly = () => {
  const [confirmingPreference, setConfirmingPreference] = useState<{
    sport: Sport;
    preference: MarketplacePreference;
    checked: boolean;
  }>();

  const updateUserProfile = useUpdateUserProfile();

  const { data, loading } = useQuery(ACCEPT_CASH_OFFERS_QUERY);

  const acceptCashOffersPreferences = useMemo(
    () =>
      (data?.currentUser?.profile.marketplacePreferences
        .map(({ sport, preferences }) => ({
          sport,
          preference: preferences.find(
            ({ name }) => name === 'cash_only_offers'
          ),
        }))
        .filter(({ preference }) => !!preference) || []) as {
        sport: Sport;
        preference: MarketplacePreference;
      }[],
    [data]
  );

  const onChange = ({
    sport,
    preference,
    checked,
  }: {
    sport: Sport;
    preference: MarketplacePreference;
    checked: boolean;
  }) => {
    updateUserProfile({
      marketplacePreferences: [
        {
          sports: [sport],
          name: preference.name,
          value: checked,
        },
      ],
    });
  };

  return (
    <SettingsSection {...messages}>
      {loading && <LoadingIndicator small />}
      {acceptCashOffersPreferences?.map(({ sport, preference }) => {
        return (
          <Option key={sport}>
            {getFormattedSport(sport)}
            <Switch
              checked={!!preference.value}
              onChange={event =>
                // onChange(sport, preference, event.target.checked)
                setConfirmingPreference({
                  sport,
                  preference,
                  checked: event.target.checked,
                })
              }
            />
          </Option>
        );
      })}
      {confirmingPreference && (
        <ConfirmDialog
          open
          onClose={() => setConfirmingPreference(undefined)}
          message={
            <BodyM className="text-center" bold>
              {confirmingPreference.checked ? (
                <FormattedMessage
                  id="Settings.AcceptCashOnly.confirmChange"
                  defaultMessage="Managers will no longer be able to propose cards as part of a trade to you, only money. Are you sure?"
                />
              ) : (
                <FormattedMessage
                  id="Settings.AcceptCashOnly.confirmDisable"
                  defaultMessage="Managers will be able to propose cards as part of a trade to you, not only money. Are you sure?"
                />
              )}
            </BodyM>
          }
          onConfirm={() => onChange(confirmingPreference)}
        />
      )}
    </SettingsSection>
  );
};
